import { Favorite, FavoriteSitterService, Sitter } from 'us-web-services';

export type TrackCallback = (event: string, params?: Record<string, unknown>) => Promise<void> | void;

export interface FavoriteToggleResult {
  bookmarked: boolean;
  favorite?: Favorite;
}

interface UseSitterSearchCardHandlersArgs {
  userId?: number;
  track: TrackCallback;
  navigateToSitter: (id: number) => void;
  navigateToBook?: (id: number) => void;
  logPrefix: string;
}

interface SitterSearchCardHandlers {
  handleCardClick: (sitter: Sitter) => void;
  handleToggleFavorite: (sitter: Sitter) => Promise<FavoriteToggleResult | null>;
  handleBook?: (sitter: Sitter) => void;
}

export const useSitterSearchCardHandlers = ({
  userId,
  track,
  navigateToSitter,
  navigateToBook,
  logPrefix,
}: UseSitterSearchCardHandlersArgs): SitterSearchCardHandlers => {
  const handleCardClick = (sitter: Sitter): void => {
    void track('clicked-button', { elementId: 'sitter-card', sitterId: sitter.id });
    if (sitter.id != null) navigateToSitter(sitter.id);
  };

  const handleToggleFavorite = async (sitter: Sitter): Promise<FavoriteToggleResult | null> => {
    if (sitter.id == null || !userId) return null;
    const isFavorited = sitter.relationship?.bookmarked ?? false;
    const favoriteId = sitter.relationship?.favorite?.id ?? sitter.relationship?.bookmarkedId;
    const nextFavorited = !isFavorited;
    if (!nextFavorited && favoriteId == null) {
      console.warn(`[${logPrefix}] cannot unfavorite: missing favorite id`);
      return null;
    }
    try {
      let favorite: Favorite | undefined;
      if (nextFavorited) {
        const resp = await FavoriteSitterService.create({ parentId: userId, sitterId: sitter.id });
        favorite = resp.data?.data;
      } else {
        await FavoriteSitterService.delete(favoriteId!);
      }
      void track('clicked-button', { elementId: 'favorite', sitterId: sitter.id });
      return { bookmarked: nextFavorited, favorite: nextFavorited ? favorite : undefined };
    } catch (err) {
      console.warn(`[${logPrefix}] favorite toggle failed`, err);
      return null;
    }
  };

  const handleBook = navigateToBook
    ? (sitter: Sitter): void => {
      if (sitter.id == null) return;
      void track('clicked-button', { elementId: 'book-cta', sitterId: sitter.id });
      navigateToBook(sitter.id);
    }
    : undefined;

  return { handleCardClick, handleToggleFavorite, handleBook };
};
